import React, { useState, useEffect } from "react";
import {
  Check,
  CheckCheck,
  Clock,
  User,
  Bot,
  Headphones,
  Image,
  File,
  Play,
  Download,
} from "lucide-react";
import MediaPreviewModal from "./ViewImageModal";

/**
 * 💬 MESSAGE BUBBLE COMPONENT
 * Displays individual message with different styles based on sender type
 */
const MessageBubble = ({ message }) => {
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Debug: Monitor state changes
  useEffect(() => {
    if (showImagePreview) {
      console.log("📱 showImagePreview state changed to:", showImagePreview);
    }
  }, [showImagePreview]);

  const {
    sender_type,
    sender_name,
    message: text,
    time,
    status,
    cost,
    model,
    is_media,
    media_type,
    media_url,
    media_info,
  } = message;

  // 🔧 HYBRID MEDIA DETECTION: Handle both correct and corrupted socket data
  const extractMediaFromMessage = (messageText) => {
    if (!messageText) return null;

    // Pattern 1: Parse embedded media_url from corrupted socket data
    const embeddedUrlMatch = messageText.match(/"media_url":\s*"([^"]+)"/);
    if (embeddedUrlMatch) {
      return {
        url: embeddedUrlMatch[1],
        type: "image", // Assume image for embedded URLs
        isEmbedded: true,
      };
    }

    // Pattern 2: Direct image URL in message text
    const directUrlMatch = messageText.match(
      /(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp|svg))/i
    );
    if (directUrlMatch) {
      return {
        url: directUrlMatch[1],
        type: "image",
        isEmbedded: false,
      };
    }

    return null;
  };

  // 🎯 DETERMINE EFFECTIVE MEDIA DATA - Improved priority handling
  const extractedMedia = extractMediaFromMessage(text);

  // Priority: media_url field > extracted from text > fallback
  const effectiveMediaUrl = media_url || extractedMedia?.url;
  const effectiveMediaType = media_type || extractedMedia?.type || "image";
  const effectiveIsMedia = is_media || !!effectiveMediaUrl;

  // 🐛 DEBUG: Log media detection for troubleshooting
  if (effectiveIsMedia || is_media) {
    console.log("🖼️ MessageBubble media detection:", {
      messageId: message.id,
      sender_type: sender_type,
      originalFields: {
        is_media: is_media,
        media_type: media_type,
        media_url: media_url,
      },
      extractedMedia: extractedMedia,
      effectiveFields: {
        effectiveIsMedia: effectiveIsMedia,
        effectiveMediaType: effectiveMediaType,
        effectiveMediaUrl: effectiveMediaUrl,
      },
    });
  }

  // Clean message text: Remove embedded media_url if found
  let cleanedText = text;
  if (extractedMedia?.isEmbedded) {
    cleanedText = text.replace(/"media_url":\s*"[^"]+"/g, "").trim();
    // Remove trailing newlines and extra whitespace
    cleanedText = cleanedText.replace(/\n+$/, "").trim();
  }

  // 🚨 PREVENT REDUNDANT TEXT: Use message text instead of caption for better visibility
  const hasMedia = effectiveIsMedia && effectiveMediaUrl;

  // Always show message text below media for better readability
  // Don't use caption system - just show the main message text
  const shouldShowMessageText = cleanedText && cleanedText.trim();

  const isCustomer = sender_type === "customer";
  const isAI = sender_type === "ai";
  const isOperator = sender_type === "operator";

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case "sent":
      case "sending":
        return <Check className="w-4 h-4 text-gray-400" />;
      case "delivered":
        return <CheckCheck className="w-4 h-4 text-gray-400" />;
      case "read":
        return <CheckCheck className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSenderIcon = () => {
    if (isCustomer) {
      return (
        <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        </div>
      );
    } else if (isAI) {
      return (
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
          <Headphones className="w-4 h-4 text-white" />
        </div>
      );
    }
  };

  const getBubbleStyle = () => {
    if (isCustomer) {
      return "bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white";
    } else if (isAI) {
      return "bg-blue-500 text-white";
    } else {
      return "bg-green-500 text-white";
    }
  };

  const getTimestampStyle = () => {
    if (isCustomer) {
      return "text-gray-500 dark:text-gray-400";
    } else if (isAI) {
      return "text-blue-100";
    } else {
      return "text-green-100";
    }
  };

  // Enhanced media rendering component
  const renderMedia = () => {
    // Use effective media data (handles both normal and corrupted cases)
    if (!effectiveIsMedia || !effectiveMediaUrl) {
      return null;
    }

    const mediaUrl = effectiveMediaUrl;
    const mediaType = effectiveMediaType;
    // Don't use caption - let message text handle the description

    switch (mediaType) {
      case "image":
        return (
          <div className="mb-2">
            <div className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer group">
              <img
                src={mediaUrl}
                alt={cleanedText || "Image"}
                className="max-w-full h-auto max-h-80 object-cover transition-all duration-200 group-hover:opacity-90 group-hover:scale-[1.02]"
                onClick={() => {
                  console.log("🖱️ Image clicked! messageId:", message.id);
                  console.log("🔍 Before state update:", {
                    showImagePreview,
                    mediaUrl: effectiveMediaUrl,
                    mediaType: effectiveMediaType,
                  });
                  setShowImagePreview(true);
                  console.log("✅ Called setShowImagePreview(true)");
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-2">
                  <Image className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </div>
              </div>
              {/* Fallback for broken images */}
              <div className="hidden items-center justify-center h-32 bg-gray-200 dark:bg-gray-700">
                <Image className="w-8 h-8 text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">
                  Image unavailable
                </span>
              </div>
            </div>
            {/* Remove caption - use main message text for better visibility */}
          </div>
        );

      case "video":
        return (
          <div className="mb-2">
            <div className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer group">
              <video
                src={mediaUrl}
                controls
                className="max-w-full h-auto max-h-80 transition-all duration-200 group-hover:opacity-90"
                onClick={(e) => {
                  // Prevent video controls from interfering
                  e.preventDefault();
                  console.log("🎬 Video clicked! messageId:", message.id);
                  console.log("🔍 Before state update:", {
                    showImagePreview,
                    mediaUrl: effectiveMediaUrl,
                    mediaType: effectiveMediaType,
                  });
                  setShowImagePreview(true);
                  console.log("✅ Called setShowImagePreview(true) for video");
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              >
                Your browser does not support video playback.
              </video>
              {/* Hover overlay for video */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-2">
                  <Play className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </div>
              </div>
              {/* Fallback for broken videos */}
              <div className="hidden items-center justify-center h-32 bg-gray-200 dark:bg-gray-700">
                <Play className="w-8 h-8 text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">
                  Video unavailable
                </span>
              </div>
            </div>
            {/* Remove caption - use main message text for better visibility */}
          </div>
        );

      case "audio":
        return (
          <div className="mb-2">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Play className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <audio
                src={mediaUrl}
                controls
                className="flex-1"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              >
                Your browser does not support audio playback.
              </audio>
              <span className="hidden text-sm text-gray-500">
                Audio unavailable
              </span>
            </div>
            {/* Remove caption - use main message text for better visibility */}
          </div>
        );

      case "document":
      case "file":
        return (
          <div className="mb-2">
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <File className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <span className="flex-1 text-sm truncate">
                {media_info?.filename || cleanedText || "Download file"}
              </span>
              <Download className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </a>
          </div>
        );

      default:
        return (
          <div className="mb-2">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
              <File className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Unsupported media type: {mediaType}
              </span>
            </div>
          </div>
        );
    }
  };

  return (
    <div
      className={`flex ${isCustomer ? "justify-start" : "justify-end"} mb-4`}
    >
      <div
        className={`max-w-xs lg:max-w-md xl:max-w-lg flex ${
          isCustomer ? "flex-row" : "flex-row-reverse"
        } items-end gap-2`}
      >
        {/* Avatar */}
        <div className="flex-shrink-0">{getSenderIcon()}</div>

        <div className={`relative px-3 py-2 rounded-lg ${getBubbleStyle()}`}>
          {/* Sender name for non-customer messages */}
          {!isCustomer && (
            <div className="text-xs opacity-70 mb-1">{sender_name}</div>
          )}

          {/* Media content */}
          {renderMedia()}

          {/* Message text - Only show if not redundant with media caption */}
          {shouldShowMessageText && cleanedText && (
            <p className="text-sm whitespace-pre-wrap">{cleanedText}</p>
          )}

          {/* Message footer */}
          <div
            className={`flex items-center gap-1 mt-1 ${
              isCustomer ? "justify-start" : "justify-end"
            }`}
          >
            <span className={`text-xs ${getTimestampStyle()}`}>{time}</span>
            {!isCustomer && (
              <div className="flex items-center">
                {getMessageStatusIcon(status)}
              </div>
            )}
          </div>

          {/* AI cost info */}
          {isAI && cost && (
            <div className="text-xs opacity-60 mt-1">
              Cost: ${cost} • {model}
            </div>
          )}

          {/* Sending indicator for optimistic messages */}
          {status === "sending" && (
            <div className="text-xs opacity-60 mt-1 italic">Sending...</div>
          )}
        </div>
      </div>

      {/* Media Preview Modal */}
      {(() => {
        const shouldRender =
          effectiveIsMedia &&
          (effectiveMediaType === "image" || effectiveMediaType === "video") &&
          effectiveMediaUrl;
        console.log("🔍 Modal render check:", {
          shouldRender,
          showImagePreview,
          effectiveIsMedia,
          effectiveMediaType,
          effectiveMediaUrl,
          messageId: message.id,
        });

        return (
          shouldRender && (
            <MediaPreviewModal
              isOpen={showImagePreview}
              onClose={() => {
                console.log("🚪 Modal onClose called");
                setShowImagePreview(false);
              }}
              mediaUrl={effectiveMediaUrl}
              mediaType={effectiveMediaType}
              mediaAlt={
                cleanedText || effectiveMediaType === "video"
                  ? "Video"
                  : "Image"
              }
              showDownload={true}
            />
          )
        );
      })()}
    </div>
  );
};

export default MessageBubble;
